import { useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { format } from "date-fns"
import { CalendarClock, CreditCard, X } from "lucide-react"
import { Organization } from "@/modules/organizations/entities/Organization"
import { getPendingSubscription } from "@/modules/subscriptions/infrastructure/subscription.api"
import { useInitiateSubscriptionPayment } from "@/modules/subscriptions/hooks/useInitiateSubscriptionPayment"
import { setPendingPaymentReturn } from "@/lib/payment-return"
import { Button } from "@/modules/shared/ui/components/button"
import { Skeleton } from "@/modules/shared/ui/components/skeleton"
import { Separator } from "@/modules/shared/ui/components/separator"

interface ReactivatePlanModalProps {
  organization: Organization | null
  isOpen: boolean
  onClose: () => void
}

export default function ReactivatePlanModal({
  organization,
  isOpen,
  onClose,
}: ReactivatePlanModalProps) {
  const {
    data: pendingSub,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["subscription", "pending", organization?.id],
    queryFn: () => getPendingSubscription(organization!.id),
    enabled: isOpen && !!organization?.id,
    retry: false,
  })

  const { mutate: initiatePayment, isPending: isInitiating } =
    useInitiateSubscriptionPayment()

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen || !organization) return null

  const handlePayNow = () => {
    if (!pendingSub) return
    setPendingPaymentReturn({ organizationId: organization.id, returnPath: "/dashboard" })
    initiatePayment(pendingSub.id)
  }

  const trialEndFormatted = pendingSub?.trialEndDate
    ? format(new Date(pendingSub.trialEndDate), "d MMMM yyyy")
    : null

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-amber-100">
              <CalendarClock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">
                Trial Expired
              </h2>
              <p className="text-sm text-muted-foreground">{organization.name}</p>
            </div>
          </div>
          <Button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <Separator />

        {/* Body */}
        <div className="p-6 space-y-5">
          <p className="text-sm text-muted-foreground">
            Your free trial for{" "}
            <span className="font-medium text-foreground">
              {organization.name}
            </span>{" "}
            has ended. Complete payment to regain access.
          </p>

          {isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          )}

          {isError && (
            <p className="text-sm text-muted-foreground text-center py-2">
              Could not load subscription details.{" "}
              <a href="/support" className="underline text-primary">
                Contact support
              </a>
              .
            </p>
          )}

          {pendingSub && (
            <>
              <div className="bg-muted rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Plan</span>
                  <span className="font-medium">{pendingSub.planName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Billing</span>
                  <span className="font-medium">
                    {pendingSub.billingInterval}
                  </span>
                </div>
                {trialEndFormatted && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Trial ended</span>
                    <span className="font-medium text-red-600">
                      {trialEndFormatted}
                    </span>
                  </div>
                )}
              </div>

              <div className="space-y-1 text-sm px-1">
                <div className="flex justify-between text-muted-foreground">
                  <span>Subtotal</span>
                  <span>R{pendingSub.price.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>VAT (15%)</span>
                  <span>R{pendingSub.vatAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold text-foreground pt-1 border-t border-border mt-1">
                  <span>Total due today</span>
                  <span>R{pendingSub.totalAmount.toFixed(2)}</span>
                </div>
              </div>

              <Button
                className="w-full"
                onClick={handlePayNow}
                disabled={isInitiating}
                style={{ backgroundColor: "#1A237E" }}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                {isInitiating ? "Redirecting to PayFast..." : "Pay Now"}
              </Button>
            </>
          )}

          <Button
            variant="ghost"
            className="w-full text-muted-foreground"
            onClick={onClose}
            disabled={isInitiating}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}
